// Queravel, SiliconBlast's assistant. Queravel keeps nothing between requests, so Rhema sends the
// recent turns with each question and keeps the history itself, encrypted on the device.

import { LinkError, type LinksApi } from "./api";

/**
 * Where Queravel lists the models the person's plan allows. The gateway allowlist on the server
 * names the same path.
 */
export const QUERAVEL_MODELS_PATH = "/api/v1/chat/models";
/**
 * The plan default Queravel reports. Queravel needs a model on every request, so this goes when
 * the device has no other: before the model list loads, or when it cannot be read.
 */
export const QUERAVEL_FALLBACK_MODEL = "kimi-k3";
/** How many earlier turns go with each request. */
export const HISTORY_TURNS = 20;

export type QueravelTurn = { role: "user" | "assistant"; text: string };

export type QueravelAction = { id: string; name: string; args: Record<string, unknown>; label: string };

export type ActionResult = {
  id: string;
  status: "done" | "rejected" | "failed" | "timeout";
  result?: unknown;
  error?: string;
};

/** One model the person's Queravel plan allows. */
export type QueravelModel = {
  id: string;
  name: string;
  default?: boolean;
  /** Set on models that come with a paid Queravel plan rather than for free. */
  plan?: boolean;
};

/** A tool Queravel is using while it answers, such as a web search. */
export type QueravelStep = {
  tool: string;
  /** What the person sees, such as "Searching the web". */
  label: string;
  /** What the tool was given, such as the search words or the page address. */
  query?: string;
  /** "call" when the tool starts, "result" once it has finished. */
  phase: "call" | "result";
};

/** A file Queravel made, such as a chart or a document. Only https addresses are kept. */
export type QueravelArtifact = { name: string; url?: string; size?: number; contentType?: string };

export type QueravelEvent =
  | { type: "start"; messageId: string; model: string; timestamp: string }
  | { type: "text"; text: string }
  | { type: "references"; references: unknown[] }
  | { type: "action"; action: QueravelAction }
  | { type: "step"; step: QueravelStep }
  /** Queravel is still working, such as while a slow tool runs. Nothing to show. */
  | { type: "heartbeat" }
  /** The last `chars` characters of the answer were narration for a tool turn and go. */
  | { type: "demote"; chars: number }
  | { type: "artifacts"; artifacts: QueravelArtifact[] }
  /** An error Queravel reported inside the stream, such as QUOTA_EXCEEDED. */
  | { type: "error"; code: string; message?: string }
  | { type: "done" };

export class QuotaExceededError extends LinkError {
  constructor() {
    super(
      "quota_exceeded",
      429,
      "queravel",
      "You have used your Queravel allowance for now. Try again later.",
    );
  }
}

/** A random id for one Queravel conversation. It is never a Rhema chat id. */
export function newQueravelChatId(): string {
  return crypto.randomUUID();
}

/** The last turns, oldest first, with empty ones dropped. */
export function buildHistory(turns: QueravelTurn[], max = HISTORY_TURNS): QueravelTurn[] {
  return turns.filter((t) => t.text.trim()).slice(-max);
}

function quote(value: unknown): string {
  return typeof value === "string" && value.trim() ? value.trim() : "";
}

/** A short label for an action, made from its name and arguments when Queravel sends none. */
export function actionLabel(name: string, args: Record<string, unknown>): string {
  switch (name) {
    case "rhema.send": {
      const to = quote(args.to);
      const files = Array.isArray(args.attachments) ? args.attachments.length : 0;
      const what = files ? (files === 1 ? "a photo" : `${files} photos`) : "a message";
      return to ? `Send ${what} to ${to}` : `Send ${what}`;
    }
    case "penielly.search": {
      // Queravel sends `query`. `q` is the earlier spelling.
      const q = quote(args.query) || quote(args.q);
      return q ? `Search your photos for "${q}"` : "Search your photos";
    }
    case "malakh.draft": {
      const to = Array.isArray(args.to) ? args.to.filter((x) => typeof x === "string").join(", ") : "";
      return to ? `Draft an email to ${to}` : "Draft an email";
    }
    default:
      return name;
  }
}

/**
 * Reads server-sent events and yields each event's data. Lines may be split across chunks. The
 * reader stops at `data: [DONE]` and cancels the rest of the stream.
 */
export async function* readSse(stream: ReadableStream<Uint8Array>): AsyncGenerator<string> {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let data: string[] = [];
  try {
    while (true) {
      const { value, done } = await reader.read();
      buffer += done ? decoder.decode() : decoder.decode(value, { stream: true });
      let nl = buffer.search(/\r?\n|\r/);
      while (nl >= 0) {
        const line = buffer.slice(0, nl);
        const skip = buffer[nl] === "\r" && buffer[nl + 1] === "\n" ? 2 : 1;
        buffer = buffer.slice(nl + skip);
        if (line === "") {
          if (data.length) {
            const payload = data.join("\n");
            data = [];
            if (payload.trim() === "[DONE]") return;
            yield payload;
          }
        } else if (line.startsWith("data:")) {
          const value = line.slice(5);
          data.push(value.startsWith(" ") ? value.slice(1) : value);
          // [DONE] ends the stream even without the blank line after it.
          if (data.length === 1 && data[0]?.trim() === "[DONE]") return;
        }
        // Comments (":") and other fields are ignored.
        nl = buffer.search(/\r?\n|\r/);
      }
      if (done) break;
    }
    if (buffer.startsWith("data:")) data.push(buffer.slice(5).trimStart());
    const rest = data.join("\n");
    if (rest && rest.trim() !== "[DONE]") yield rest;
  } finally {
    await reader.cancel().catch(() => undefined);
  }
}

const TOOL_LABELS: Record<string, string> = {
  web_search: "Searching the web",
  fetch_url: "Reading a page",
  run_code: "Running code",
  geocode: "Checking the map",
  route: "Checking the map",
  generate_image: "Making an image",
};

/** The fixed label for a tool, for when Queravel gives no intent. */
export function toolLabel(tool: string): string {
  return TOOL_LABELS[tool] ?? "Working";
}

const MAX_LABEL = 80;

function clip(text: string, max: number): string {
  const t = text.replace(/\s+/g, " ").trim();
  return t.length > max ? `${t.slice(0, max - 1).trimEnd()}…` : t;
}

/** What a tool was given, in a few words: the search, the page or the place. Code is not shown. */
function queryOf(args: Record<string, unknown>): string {
  for (const key of ["query", "q", "url", "address", "place", "destination"]) {
    const v = quote(args[key]);
    if (v) return clip(v, MAX_LABEL);
  }
  return "";
}

function stepOf(raw: Record<string, unknown>): QueravelStep | null {
  const type = raw.type;
  if ((type !== "tool_call" && type !== "tool_result") || typeof raw.tool !== "string" || !raw.tool)
    return null;
  const tool = raw.tool;
  const args = raw.args && typeof raw.args === "object" ? (raw.args as Record<string, unknown>) : {};
  const intent = quote(args.intent);
  const query = queryOf(args);
  return {
    tool,
    label: intent ? clip(intent, MAX_LABEL) : toolLabel(tool),
    ...(query ? { query } : {}),
    phase: type === "tool_call" ? "call" : "result",
  };
}

function artifactOf(value: unknown): QueravelArtifact | null {
  if (!value || typeof value !== "object") return null;
  const a = value as Record<string, unknown>;
  const name = quote(a.name) || quote(a.title);
  if (!name) return null;
  const url = typeof a.url === "string" && /^https:\/\//i.test(a.url) ? a.url : undefined;
  const size = typeof a.size === "number" && Number.isFinite(a.size) && a.size >= 0 ? a.size : undefined;
  const type = quote(a.content_type) || quote(a.contentType);
  return {
    name,
    ...(url ? { url } : {}),
    ...(size !== undefined ? { size } : {}),
    ...(type ? { contentType: type } : {}),
  };
}

/** The files Queravel made, in the shape Rhema keeps. Anything without a name is dropped. */
export function artifactsOf(list: unknown): QueravelArtifact[] {
  if (!Array.isArray(list)) return [];
  return list.map(artifactOf).filter((a): a is QueravelArtifact => a !== null);
}

/** Turns one SSE data payload into events. Unknown payloads give nothing. */
export function parseQueravelData(payload: string): QueravelEvent[] {
  let data: Record<string, unknown>;
  try {
    data = JSON.parse(payload);
  } catch {
    return [];
  }
  if (!data || typeof data !== "object") return [];
  const events: QueravelEvent[] = [];
  if (typeof data.error === "string" || data.quota_exceeded === true) {
    const code = data.quota_exceeded === true ? "QUOTA_EXCEEDED" : String(data.error);
    const message = typeof data.message === "string" ? data.message : undefined;
    return [{ type: "error", code, ...(message ? { message } : {}) }];
  }
  if (typeof data.messageId === "string") {
    events.push({
      type: "start",
      messageId: data.messageId,
      model: typeof data.model === "string" ? data.model : "",
      timestamp: typeof data.timestamp === "string" ? data.timestamp : "",
    });
  }
  if (typeof data.text === "string" && data.text) events.push({ type: "text", text: data.text });
  if (Array.isArray(data.references)) events.push({ type: "references", references: data.references });
  const agentStep = data.agent_step as Record<string, unknown> | undefined;
  if (agentStep && typeof agentStep === "object") {
    const step = stepOf(agentStep);
    if (step) events.push({ type: "step", step });
    else if (agentStep.type === "reasoning") events.push({ type: "heartbeat" });
  }
  if (typeof data.answer_demote === "number" && data.answer_demote > 0)
    events.push({ type: "demote", chars: Math.floor(data.answer_demote) });
  if (Array.isArray(data.artifacts))
    events.push({ type: "artifacts", artifacts: artifactsOf(data.artifacts) });
  const action = data.action as Record<string, unknown> | undefined;
  if (
    action &&
    typeof action === "object" &&
    typeof action.id === "string" &&
    typeof action.name === "string"
  ) {
    const args =
      action.args && typeof action.args === "object" ? (action.args as Record<string, unknown>) : {};
    events.push({
      type: "action",
      action: {
        id: action.id,
        name: action.name,
        args,
        label: quote(action.label) || actionLabel(action.name, args),
      },
    });
  }
  return events;
}

export type QueravelRequest = {
  chatId: string;
  /** The new question. Left out when the request only carries an action result. */
  message?: string;
  history: QueravelTurn[];
  /** The model to answer with. Without one, the fallback model goes, as Queravel needs a model. */
  model?: string;
  actionResult?: ActionResult;
  signal?: AbortSignal;
  /**
   * The Rhema chat the question is about, when it came from one. The Rhema server checks that
   * Queravel is allowed there. Queravel never sees it.
   */
  about?: string;
  /**
   * Names the answer so the server reads it to the end whatever this device does, and keeps it
   * for 15 minutes, encrypted with `key`. The device can then read it again with `resume`.
   */
  stream?: KeptStream;
};

/** A random id and a random 32-byte key, base64, that name one kept answer. */
export type KeptStream = { id: string; key: string };

/** A new id and key for a kept answer. */
export function newKeptStream(): KeptStream {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  let raw = "";
  for (const b of bytes) raw += String.fromCharCode(b);
  return { id: crypto.randomUUID(), key: btoa(raw) };
}

/**
 * Reads Queravel's model list, `{models: [{id, name, provider, vision, context, tier, free}],
 * default_model}`. The model named by `default_model` is marked as the default. A model is a plan
 * model when it is not free.
 */
export function modelsOf(data: unknown): QueravelModel[] {
  const obj =
    data && typeof data === "object" && !Array.isArray(data) ? (data as Record<string, unknown>) : {};
  const list = Array.isArray(data) ? data : Array.isArray(obj.models) ? obj.models : [];
  const fallback = quote(obj.default_model);
  const out: QueravelModel[] = [];
  for (const item of list) {
    if (!item || typeof item !== "object") continue;
    const m = item as Record<string, unknown>;
    const id = quote(m.id);
    if (!id || out.some((x) => x.id === id)) continue;
    const isDefault = fallback ? id === fallback : m.default === true;
    const plan = m.free === false || (m.free !== true && typeof m.tier === "string" && !!m.tier);
    out.push({
      id,
      name: quote(m.name) || id,
      ...(isDefault ? { default: true } : {}),
      ...(plan ? { plan: true } : {}),
    });
  }
  return out;
}

export class QueravelLink {
  constructor(private readonly links: LinksApi) {}

  /** Builds the request body Queravel expects. */
  static body(req: QueravelRequest): Record<string, unknown> {
    return {
      chat_id: req.chatId,
      ...(req.message !== undefined ? { message: req.message } : {}),
      history: buildHistory(req.history),
      model: req.model || QUERAVEL_FALLBACK_MODEL,
      user_id: "ignored",
      client_context: { client: "rhema" },
      ...(req.actionResult ? { action_result: req.actionResult } : {}),
    };
  }

  /** The models the person's Queravel plan allows. */
  async listModels(): Promise<QueravelModel[]> {
    return modelsOf(await this.links.gatewayJson<unknown>("queravel", "GET", QUERAVEL_MODELS_PATH));
  }

  /** Streams one answer. Ends with a "done" event. */
  async *stream(req: QueravelRequest): AsyncGenerator<QueravelEvent> {
    let res: Response;
    try {
      res = await this.links.gateway("queravel", "POST", "/api/v1/chat/stream", {
        json: QueravelLink.body(req),
        headers: {
          Accept: "text/event-stream",
          ...(req.stream ? { "X-Rhema-Stream": req.stream.id, "X-Rhema-Stream-Key": req.stream.key } : {}),
          ...(req.about ? { "X-Rhema-Chat": req.about } : {}),
        },
        signal: req.signal,
      });
    } catch (err) {
      if (err instanceof LinkError && err.code === "quota_exceeded") throw new QuotaExceededError();
      throw err;
    }
    yield* QueravelLink.events(res);
  }

  /**
   * Reads a kept answer again from its start: after the screen that asked was left, the connection
   * dropped or the phone put the app to sleep. Throws a LinkError "not_found" once it has expired.
   */
  async *resume(stream: KeptStream, signal?: AbortSignal): AsyncGenerator<QueravelEvent> {
    const res = await this.links.api.raw("GET", `/api/link-streams/${stream.id}`, {
      headers: { Accept: "text/event-stream", "X-Rhema-Stream-Key": stream.key },
      ...(signal ? { signal } : {}),
    });
    if (!res.ok) throw new LinkError(res.status === 404 ? "not_found" : "failed", res.status, "queravel");
    yield* QueravelLink.events(res);
  }

  /** Asks the server to stop reading an answer the person stopped. */
  async stop(stream: KeptStream): Promise<void> {
    await this.links.api.raw("DELETE", `/api/link-streams/${stream.id}`).catch(() => undefined);
  }

  private static async *events(res: Response): AsyncGenerator<QueravelEvent> {
    const type = res.headers.get("Content-Type") ?? "";
    if (!type.includes("text/event-stream") && type.includes("json")) {
      const data = (await res.json().catch(() => ({}))) as { quota_exceeded?: boolean; status?: string };
      if (data.quota_exceeded) throw new QuotaExceededError();
      throw new LinkError("failed", res.status, "queravel");
    }
    if (!res.body) throw new LinkError("failed", res.status, "queravel");
    for await (const payload of readSse(res.body)) {
      for (const event of parseQueravelData(payload)) {
        if (event.type === "error") {
          // Quota can come back inside the stream as well as a plain JSON body.
          if (event.code.toUpperCase() === "QUOTA_EXCEEDED") throw new QuotaExceededError();
          throw new LinkError("failed", res.status, "queravel", "Queravel could not answer. Try again.");
        }
        yield event;
      }
    }
    yield { type: "done" };
  }
}
